import React, { useContext, useEffect, useState } from 'react';

import { BBox } from './BBox';
import { FlipButton } from './FlipButton';
import { SceneCanvasContext, ViewMode } from './SceneCanvasContext';
import { Character, CharacterOrientation } from './types';
import { useCharacterDrag } from './useCharacterDrag';
import { CHAR_IMAGE_TEXT_OFFSET } from './utils';

interface SVGCharacterProps {
  characterId: Character['id'];
}

export const SVGCharacter: React.FC<SVGCharacterProps> = ({ characterId }) => {
  const {
    viewMode,

    getCharacterById,

    characterInFocus,
    setCharacterInFocus,
    clearCharacterFocus,

    scaleCharacter,
  } = useContext(SceneCanvasContext);

  const {
    name,
    position,
    width,
    height,
    assetPath,
    orientation,
  } = getCharacterById(characterId);

  const [isTheMouseOverMe, setIsTheMouseOverMe] = useState(false);

  const { startDrag, drag, stopDrag } = useCharacterDrag();

  const [isBeingScaled, setIsBeingScaled] = useState(false);

  useEffect(() => {
    if (characterInFocus === null && !isTheMouseOverMe) {
      setIsTheMouseOverMe(false);
    }
  }, [characterInFocus, isTheMouseOverMe]);

  return (
    <>
      <svg
        id={characterId}
        style={{ overflow: 'visible' }}
        x={position.x}
        y={position.y}
        width={width}
        height={height}
      >
        <g
          onMouseEnter={() => {
            if (viewMode !== ViewMode.EDIT) {
              return;
            }
            if (characterInFocus === null) {
              setIsTheMouseOverMe(true);
            }
          }}
          onMouseLeave={() => {
            if (viewMode !== ViewMode.EDIT) {
              return;
            }
            setIsTheMouseOverMe(false);
          }}
        >
          <image
            style={viewMode === ViewMode.EDIT ? { cursor: 'move' } : undefined}
            alignmentBaseline="baseline"
            href={assetPath}
            width="100%"
            height="100%"
            x={0}
            y={0}
            transform={
              orientation === CharacterOrientation.FLIPPED ? `scale(-1,1)` : ''
            }
            transform-origin="center"
            onMouseDown={(event) => {
              if (viewMode !== ViewMode.EDIT) {
                return;
              }

              setCharacterInFocus(characterId);
              startDrag(position.x, position.y, event);
              event.preventDefault();
            }}
            onMouseMove={(event) => {
              if (viewMode !== ViewMode.EDIT) {
                return;
              }

              drag(event, (newX, newY) => {
                scaleCharacter(characterId, newX, newY, null, null);
              });
            }}
            onMouseUp={() => {
              if (viewMode !== ViewMode.EDIT) {
                return;
              }

              clearCharacterFocus();
              stopDrag();
            }}
            onMouseLeave={() => {
              if (viewMode !== ViewMode.EDIT) {
                return;
              }

              stopDrag();
            }}
          />
          {viewMode === ViewMode.EDIT && (isTheMouseOverMe || isBeingScaled) && (
            <>
              <BBox
                characterId={characterId}
                scaleStartCallback={() => {
                  setCharacterInFocus(characterId);
                  setIsBeingScaled(true);
                }}
                scalingCallback={(newX, newY, newWidth, newHeight) => {
                  scaleCharacter(characterId, newX, newY, newWidth, newHeight);
                }}
                scaleEndCallback={() => {
                  clearCharacterFocus();
                  setIsBeingScaled(false);
                }}
              />
              <FlipButton characterId={characterId} />
            </>
          )}
        </g>
        <text
          style={{ fontFamily: 'Roboto', fontSize: '0.75rem' }}
          textAnchor="middle"
          x={width / 2}
          y={height + CHAR_IMAGE_TEXT_OFFSET}
        >
          {name}
        </text>
      </svg>
    </>
  );
};
